/**
 * 分组容器节点：虚线透明容器，把画布区域内的节点组织为一个组。
 *
 * 成员关系：按"节点中心是否落在容器矩形内"动态判定（分组节点自身除外）。
 *  - 拖动容器 → 全部成员一起移动
 *  - 删除容器 → 成员原地保留（自动解组）
 *  - 四角手柄调整容器大小
 *  - 双击标签改名、右键菜单（复制/信息/删除）
 *
 * 智能体批次外框（_autoGenerated，见 agentUnit）：成员改按 agent 数据判定
 * （避免把恰好重叠进框内的无关节点一起拖走），隐藏缩放手柄（尺寸由系统
 * 管理），删除走整批确认（ContextMenu 冻结单元守卫）。
 */
import { useRef, useState } from 'react';
import { Layers } from 'lucide-react';
import type { CanvasNode, GroupNodeData } from '../types';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import { isNodeInsideGroup } from '../utils';
import { findAgentUnitOf } from '../generators/agentUnit';
import { useContextMenu, ContextMenu } from '../ContextMenu';
import { NodeInfoDialog } from '../NodeInfoDialog';

interface GroupNodeProps {
  node: CanvasNode<GroupNodeData>;
  selected: boolean;
  highlighted: boolean;
}

export function GroupNode({ node, selected, highlighted }: GroupNodeProps) {
  const nodes = useBoard((s) => s.nodes);
  // 智能体批次外框按 agent 数据成员；普通分组按几何包含（中心落框内）
  const claimingUnit = findAgentUnitOf(node.id, nodes);
  const isAgentContainer = claimingUnit?.containerId === node.id;
  const memberIds = isAgentContainer
    ? claimingUnit.memberIds
    : nodes.filter((n) => isNodeInsideGroup(n, node)).map((n) => n.id);

  const { menuState, onContextMenu, closeMenu } = useContextMenu();
  const [infoOpen, setInfoOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');

  const dragRef = useRef<{
    startX: number;
    startY: number;
    ids: string[];
    initial: Record<string, { x: number; y: number }>;
  } | null>(null);
  const resizeRef = useRef<{
    corner: 'nw' | 'ne' | 'sw' | 'se';
    startX: number;
    startY: number;
    rect: { x: number; y: number; width: number; height: number };
  } | null>(null);

  const label = (node.data.label || '').trim() || (typeof (node.data as Record<string, unknown>)._title === 'string' ? ((node.data as Record<string, unknown>)._title as string).trim() : '') || '分组';

  // ---------------- 容器拖拽（连带成员） ----------------
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-group-no-drag]')) return;
    e.stopPropagation();

    const cur = getBoardState();
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      const nextNodeIds = cur.selection.nodeIds.includes(node.id)
        ? cur.selection.nodeIds.filter((id) => id !== node.id)
        : [...cur.selection.nodeIds, node.id];
      boardActions.setSelection({ nodeIds: nextNodeIds, edgeIds: [] });
    } else if (!cur.selection.nodeIds.includes(node.id)) {
      boardActions.setSelection({ nodeIds: [node.id], edgeIds: [] });
    }

    // 移动对象 = 容器 + 全部成员
    const ids = [node.id, ...memberIds];
    const after = getBoardState();
    const initial: Record<string, { x: number; y: number }> = {};
    after.nodes.forEach((n) => {
      if (ids.includes(n.id)) initial[n.id] = { x: n.x, y: n.y };
    });
    dragRef.current = { startX: e.clientX, startY: e.clientY, ids, initial };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const zoom = getBoardState().viewport.zoom;
    const dx = (e.clientX - drag.startX) / zoom;
    const dy = (e.clientY - drag.startY) / zoom;
    drag.ids.forEach((id) => {
      const init = drag.initial[id];
      if (!init) return;
      boardActions.updateNode(id, { x: init.x + dx, y: init.y + dy });
    });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const moved =
      Math.abs(e.clientX - dragRef.current.startX) > 1 ||
      Math.abs(e.clientY - dragRef.current.startY) > 1;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    if (moved) boardActions.commitNodeMutation();
  };

  // ---------------- 容器缩放（四角手柄） ----------------
  const startResize = (corner: 'nw' | 'ne' | 'sw' | 'se') => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resizeRef.current = {
      corner,
      startX: e.clientX,
      startY: e.clientY,
      rect: { x: node.x, y: node.y, width: node.width, height: node.height },
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onResizeMove = (e: React.PointerEvent) => {
    const resize = resizeRef.current;
    if (!resize) return;
    const zoom = getBoardState().viewport.zoom;
    const dx = (e.clientX - resize.startX) / zoom;
    const dy = (e.clientY - resize.startY) / zoom;
    const MIN = 160;
    let { x, y, width, height } = resize.rect;
    if (resize.corner === 'se') {
      width = Math.max(MIN, resize.rect.width + dx);
      height = Math.max(120, resize.rect.height + dy);
    } else if (resize.corner === 'ne') {
      width = Math.max(MIN, resize.rect.width + dx);
      const bottom = resize.rect.y + resize.rect.height;
      height = Math.max(120, resize.rect.height - dy);
      y = bottom - height;
    } else if (resize.corner === 'sw') {
      const right = resize.rect.x + resize.rect.width;
      width = Math.max(MIN, resize.rect.width - dx);
      x = right - width;
      height = Math.max(120, resize.rect.height + dy);
    } else {
      const right = resize.rect.x + resize.rect.width;
      const bottom = resize.rect.y + resize.rect.height;
      width = Math.max(MIN, resize.rect.width - dx);
      height = Math.max(120, resize.rect.height - dy);
      x = right - width;
      y = bottom - height;
    }
    boardActions.updateNode(node.id, { x, y, width, height });
  };

  const endResize = () => {
    if (resizeRef.current) {
      resizeRef.current = null;
      boardActions.commitNodeMutation();
    }
  };

  const commitLabel = () => {
    setEditingLabel(false);
    boardActions.updateNodeData(node.id, { label: labelDraft.trim() });
    boardActions.commitNodeMutation();
  };

  return (
    <div
      data-canvas-node-id={node.id}
      className={`group absolute select-none rounded-2xl transition-shadow ${
        selected
          ? 'border-2 border-dashed border-blue-500 bg-blue-500/[0.04]'
          : highlighted
            ? 'border-2 border-dashed border-blue-300 bg-blue-500/[0.03]'
            : 'border-2 border-dashed border-slate-300 bg-slate-500/[0.03] hover:border-slate-400'
      }`}
      style={{
        left: node.x,
        top: node.y,
        width: node.width,
        height: node.height,
        // 选中也不抬升层级：容器抬到成员之上会盖住成员导致其无法交互，
        // 选中态靠虚线边框表达（NodeRenderer 先渲染分组，成员天然在容器之上）。
        zIndex: node.zIndex,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onContextMenu={(e) => onContextMenu(e, node.id)}
    >
      {/* 标签 */}
      <div
        className="absolute -top-3 left-4 flex max-w-[calc(100%-2rem)] items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm"
        data-group-no-drag
      >
        <Layers className="h-3 w-3 shrink-0 text-slate-400" />
        {editingLabel ? (
          <input
            autoFocus
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitLabel();
              if (e.key === 'Escape') setEditingLabel(false);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-28 rounded-md border border-blue-400 px-1 text-[11px] text-slate-700 outline-none"
            maxLength={30}
          />
        ) : (
          <span
            className="cursor-text truncate text-[11px] font-medium text-slate-500"
            title={label}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setLabelDraft(node.data.label || '');
              setEditingLabel(true);
            }}
          >
            {label}
            <span className="ml-1.5 text-slate-300">{memberIds.length}</span>
          </span>
        )}
      </div>

      {/* 四角缩放手柄（智能体批次外框尺寸由系统管理，不显示） */}
      {!node.data._autoGenerated &&
        ([
          ['nw', '-left-1 -top-1 cursor-nw-resize'],
          ['ne', '-right-1 -top-1 cursor-ne-resize'],
          ['sw', '-bottom-1 -left-1 cursor-sw-resize'],
          ['se', '-bottom-1 -right-1 cursor-se-resize'],
        ] as const).map(([corner, cls]) => (
          <div
            key={corner}
            className={`absolute h-3 w-3 rounded-full border-2 border-white bg-blue-400 opacity-0 shadow transition group-hover:opacity-100 ${cls}`}
            data-group-no-drag
            onPointerDown={startResize(corner)}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
          />
        ))}

      {/* 右键菜单与信息弹窗 */}
      {menuState && (
        <ContextMenu
          state={menuState}
          onClose={closeMenu}
          onInfo={() => {
            closeMenu();
            setInfoOpen(true);
          }}
        />
      )}
      {infoOpen && <NodeInfoDialog node={node} onClose={() => setInfoOpen(false)} />}
    </div>
  );
}
